<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import backendCall from '@/services/AxiosService';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Skeleton from 'primevue/skeleton';
import Tag from 'primevue/tag';

interface BoSwapMovement {
  movement_id: number;
  genset_number: string;
  yard: string;
  in_time: string;
  in_type: string;
  out_time: string;
  out_type: string;
  swapped_out_genset: string;
  recorded_in_by: string;
  recorded_out_by: string;
}

const toast = useToast();
const boSwapMovements = ref<BoSwapMovement[]>([]);
const loading = ref(false);

const fetchBoSwapMovements = async () => {
  loading.value = true;
  try {
    const response = await backendCall.get('power-pack/bo-swaps');
    if (response.data.success) {
      boSwapMovements.value = response.data.data;
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch BO swap movements',
        life: 3000
      });
    }
  } catch (error) {
    console.error('Error fetching BO swap movements:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch BO swap movements',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
};

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Winnipeg'
  }).format(date);
};

onMounted(() => {
  fetchBoSwapMovements();
});
</script>

<template>
  <div class="p-6">
    <Card>
      <template #title>
        <div class="flex justify-between items-center">
          <div class="font-medium text-3xl text-surface-900 dark:text-surface-0">
            BO Swap Powerpacks
          </div>
          <Button 
            icon="pi pi-refresh" 
            @click="fetchBoSwapMovements" 
            :loading="loading"
            outlined
            rounded
          />
        </div>
      </template>
      
      <template #content>
        <div class="mb-4 text-surface-600 dark:text-surface-400">
          Bad Order powerpacks that have been swapped out for good powerpacks
        </div>
        
        <DataTable 
          :value="boSwapMovements" 
          :loading="loading"
          stripedRows
          responsiveLayout="scroll"
          class="p-datatable-sm"
          paginator
          :rows="25"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          :rowsPerPageOptions="[10, 25, 50]"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        >
          <template #loading>
            <div class="grid">
              <div class="col-12" v-for="i in 10" :key="i">
                <Skeleton class="mb-2" borderRadius="16px" height="4rem"></Skeleton>
              </div>
            </div>
          </template>
          
          <Column field="genset_number" header="Bad Order Genset" sortable>
            <template #body="slotProps">
              <div class="font-semibold text-red-600 dark:text-red-400">
                {{ slotProps.data.genset_number }}
              </div>
            </template>
          </Column>
          
          <Column field="swapped_out_genset" header="Replacement Genset" sortable>
            <template #body="slotProps">
              <div class="font-medium text-green-600 dark:text-green-400">
                {{ slotProps.data.swapped_out_genset }}
              </div>
            </template>
          </Column>
          
          <Column field="yard" header="Yard" sortable>
            <template #body="slotProps">
              <Tag :value="slotProps.data.yard" severity="info"></Tag>
            </template>
          </Column>
          
          <Column field="in_time" header="Bad Order Original In Time" sortable>
            <template #body="slotProps">
              <div class="text-sm">
                {{ formatDateTime(slotProps.data.in_time) }}
              </div>
            </template>
          </Column>
          
          <Column field="in_type" header="Bad Order Original In Type" sortable>
            <template #body="slotProps">
              <Tag 
                :value="slotProps.data.in_type" 
                :severity="slotProps.data.in_type === 'deadhead' ? 'warning' : 'secondary'"
              ></Tag>
            </template>
          </Column>
          
          <Column field="out_time" header="BO Swap Time" sortable>
            <template #body="slotProps">
              <div class="text-sm font-medium text-orange-600 dark:text-orange-400">
                {{ formatDateTime(slotProps.data.out_time) }}
              </div>
            </template>
          </Column>
          
          <Column field="recorded_in_by" header="Recorded In By" sortable>
            <template #body="slotProps">
              <div class="text-sm">
                {{ slotProps.data.recorded_in_by }}
              </div>
            </template>
          </Column>
          
          <Column field="recorded_out_by" header="BO Swap Recorded By" sortable>
            <template #body="slotProps">
              <div class="text-sm">
                {{ slotProps.data.recorded_out_by }}
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.p-datatable-sm :deep(.p-datatable-tbody > tr > td) {
  padding: 0.5rem;
}
</style> 